import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import AdminLayout from './components/layout/AdminLayout';
import Dashboard from './components/dashboard/Dashboard';
import Login from './components/auth/Login';
import SettingsPage from './components/auth/SettingsPage';
import Bookings from './components/bookings/Bookings';
import Rooms from './components/rooms/Rooms';
import RoomWorkflows from './components/rooms/RoomWorkflows';
import RestaurantReservations from './components/services/RestaurantReservations';
import RestaurantPOS from './components/services/RestaurantPOS';
import SportsActivityReservations from './components/services/SportsActivityReservations';
import ContactSubmissions from './components/services/ContactSubmissions';
import WebsiteContentManager from './components/services/WebsiteContentManager';
import NewsletterManagement from './components/services/NewsletterManagement';
import LaundryService from './components/services/LaundryService';
import FrontDesk from './components/frontdesk/FrontDesk';
import OperationsSuite from './components/operations/OperationsSuite';
import CalendarPage from './components/reservations/CalendarPage';
import StaffManagement from './components/staff/StaffManagement';
import GuestCRM from './components/crm/GuestCRM';
import ReportsPage from './components/reports/ReportsPage';
import FinanceDashboard from './components/finance/FinanceDashboard';
import VouchersPage from './components/finance/VouchersPage';
import VoucherDetailPage from './components/finance/VoucherDetailPage';
import ApprovalsPage from './components/finance/ApprovalsPage';
import DailyClosePage from './components/finance/DailyClosePage';
import BankingPage from './components/finance/BankingPage';
import FinanceSettingsPage from './components/finance/FinanceSettingsPage';
import PurchasesPage from './components/finance/PurchasesPage';
import InventoryPage from './components/finance/InventoryPage';
import PayrollPage from './components/finance/PayrollPage';
import AuditLogPage from './components/audit/AuditLogPage';
import PrinterSettings from './components/settings/PrinterSettings';
import './App.css';
import { RoomPackageManagement } from './components/rooms/room-packages';
import { getPermissions, hasRole } from './utils/permissions';

// Check if user is authenticated
const isAuthenticated = () => localStorage.getItem('admin_token') !== null;

// Menu-order fallback used when a user has no access to a requested route.
// Each entry mirrors AdminLayout's menu order; the first permitted path wins,
// so a kitchen user lands on the POS instead of bouncing off /dashboard.
const ROUTE_FALLBACKS: Array<{ path: string; permission: string }> = [
  { path: '/dashboard', permission: 'dashboard:view' },
  { path: '/front-desk', permission: 'bookings:edit' },
  { path: '/bookings', permission: 'bookings:view' },
  { path: '/rooms', permission: 'rooms:view' },
  { path: '/guests', permission: 'guests:view' },
  { path: '/services/restaurant-pos', permission: 'restaurant:view' },
  { path: '/services/sports-activities', permission: 'activities:view' },
  { path: '/services/laundry', permission: 'laundry:view' },
  { path: '/billing', permission: 'billing:view' },
  { path: '/calendar/advanced', permission: 'bookings:view' },
  { path: '/notifications', permission: 'notifications:view' },
  { path: '/staff', permission: 'staff:view' },
  { path: '/finance', permission: 'finance:view' },
  { path: '/reports', permission: 'reports:view' },
  { path: '/settings', permission: 'settings:view' },
];

// First route the current user may open. '/dashboard' is skipped as a target
// (it is the login landing page and must never be a redirect goal, or a user
// without dashboard:view would loop). '/login' is the loop-free terminal when
// no permission matches at all.
const firstPermittedRoute = (permissions: string[]): string => {
  const match = ROUTE_FALLBACKS.find(
    (r) => r.path !== '/dashboard' && permissions.includes(r.permission),
  );
  return match ? match.path : '/login';
};

// Redirect to login if not authenticated
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return isAuthenticated() ? <>{children}</> : <Navigate to="/login" replace />;
};

// Redirect to the first permitted route if the user lacks a required permission
const PermissionRoute: React.FC<{ permission: string; children: React.ReactNode }> = ({ permission, children }) => {
  const permissions = getPermissions();
  if (permissions.includes(permission)) return <>{children}</>;
  return <Navigate to={firstPermittedRoute(permissions)} replace />;
};

// Universal landing page: shown only to users holding dashboard:view.
// Everyone else is bounced to their first permitted route — this prevents a
// kitchen session from looping back to /dashboard after login.
const DashboardRoute: React.FC = () => {
  const permissions = getPermissions();
  if (permissions.includes('dashboard:view')) return <Dashboard />;
  return <Navigate to={firstPermittedRoute(permissions)} replace />;
};

// The backend audit routes require the admin role, not just a permission —
// a manager holding settings:view still gets 403 there.
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (hasRole('admin')) return <>{children}</>;
  return <Navigate to={firstPermittedRoute(getPermissions())} replace />;
};

// Placeholder components for ops sub-sections (still in OperationsSuite)
const BillingPage = () => <OperationsSuite defaultTab="billing" />;
const CalendarEnginePage = () => <CalendarPage />;
const NotificationPage = () => <OperationsSuite defaultTab="notifications" />;
const SportsOperationsPage = () => <OperationsSuite defaultTab="sports" />;

function App() {
  // Custom theme configuration
  const theme = {
    token: {
      colorPrimary: '#b79a68',
      colorSuccess: '#52c41a',
      colorWarning: '#faad14',
      colorError: '#a5171f',
      colorInfo: '#1890ff',
      borderRadius: 6,
      colorBgContainer: '#ffffff',
    },
    components: {
      Layout: {
        bodyBg: '#f5f5f5',
        headerBg: '#ffffff',
        siderBg: '#ffffff',
      },
      Menu: {
        itemSelectedBg: 'rgba(183, 154, 104, 0.15)',
        itemSelectedColor: '#b79a68',
        itemHoverBg: 'rgba(183, 154, 104, 0.1)',
      },
    },
  };

  return (
    <ConfigProvider theme={theme}>
      <Router>
        <div className="App">
          <Routes>
            {/* Login Route */}
            <Route path="/login" element={<Login />} />

            {/* Protected Routes with Admin Layout */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<DashboardRoute />} />
                      <Route path="/front-desk" element={<PermissionRoute permission="bookings:edit"><FrontDesk /></PermissionRoute>} />
                      <Route path="/bookings" element={<PermissionRoute permission="bookings:view"><Bookings /></PermissionRoute>} />
                      <Route path="/rooms/*" element={<PermissionRoute permission="rooms:view"><Rooms /></PermissionRoute>} />
                      <Route path="/rooms/workflows" element={<PermissionRoute permission="rooms:view"><RoomWorkflows /></PermissionRoute>} />
                      <Route path="/services/restaurant-pos" element={<PermissionRoute permission="restaurant:view"><RestaurantPOS /></PermissionRoute>} />
                      <Route path="/services/restaurant-reservations" element={<PermissionRoute permission="restaurant:view"><RestaurantReservations /></PermissionRoute>} />
                      <Route path="/services/sports-activities" element={<PermissionRoute permission="activities:view"><SportsActivityReservations /></PermissionRoute>} />
                      <Route path="/services/sports-operations" element={<PermissionRoute permission="activities:view"><SportsOperationsPage /></PermissionRoute>} />
                      <Route path="/services/contact-submissions" element={<PermissionRoute permission="restaurant:view"><ContactSubmissions /></PermissionRoute>} />
                      <Route path="/services/website-content" element={<PermissionRoute permission="restaurant:view"><WebsiteContentManager /></PermissionRoute>} />
                      <Route path="/services/newsletter" element={<PermissionRoute permission="restaurant:view"><NewsletterManagement /></PermissionRoute>} />
                      <Route path="/services/laundry" element={<PermissionRoute permission="laundry:view"><LaundryService /></PermissionRoute>} />
                      <Route path="/billing" element={<PermissionRoute permission="billing:view"><BillingPage /></PermissionRoute>} />
                      <Route path="/calendar/advanced" element={<PermissionRoute permission="bookings:view"><CalendarEnginePage /></PermissionRoute>} />
                      <Route path="/notifications" element={<PermissionRoute permission="notifications:view"><NotificationPage /></PermissionRoute>} />
                      <Route path="/guests" element={<PermissionRoute permission="guests:view"><GuestCRM /></PermissionRoute>} />
                      <Route path="/services" element={<Navigate to="/services/restaurant-reservations" replace />} />
                      <Route path="/staff" element={<PermissionRoute permission="staff:view"><StaffManagement /></PermissionRoute>} />
                      <Route path="/reports" element={<PermissionRoute permission="reports:view"><ReportsPage /></PermissionRoute>} />
                      <Route path="/finance" element={<PermissionRoute permission="finance:view"><FinanceDashboard /></PermissionRoute>} />
                      <Route path="/finance/vouchers" element={<PermissionRoute permission="finance:view"><VouchersPage /></PermissionRoute>} />
                      <Route path="/finance/vouchers/:id" element={<PermissionRoute permission="finance:view"><VoucherDetailPage /></PermissionRoute>} />
                      <Route path="/finance/approvals" element={<PermissionRoute permission="finance:approve"><ApprovalsPage /></PermissionRoute>} />
                      <Route path="/finance/close" element={<PermissionRoute permission="finance:view"><DailyClosePage /></PermissionRoute>} />
                      <Route path="/finance/banking" element={<PermissionRoute permission="finance:view"><BankingPage /></PermissionRoute>} />
                      <Route path="/finance/purchases" element={<PermissionRoute permission="finance:view"><PurchasesPage /></PermissionRoute>} />
                      <Route path="/finance/inventory" element={<PermissionRoute permission="inventory:view"><InventoryPage /></PermissionRoute>} />
                      <Route path="/finance/payroll" element={<PermissionRoute permission="payroll:view"><PayrollPage /></PermissionRoute>} />
                      <Route path="/finance/settings" element={<PermissionRoute permission="finance:view"><FinanceSettingsPage /></PermissionRoute>} />
                      <Route path="/settings" element={<PermissionRoute permission="settings:view"><SettingsPage /></PermissionRoute>} />
                      <Route path="/audit" element={<AdminRoute><PermissionRoute permission="settings:view"><AuditLogPage /></PermissionRoute></AdminRoute>} />
                      <Route path="/settings/printers" element={<PermissionRoute permission="settings:view"><PrinterSettings /></PermissionRoute>} />
                      <Route path="/room-packages" element={<PermissionRoute permission="rooms:view"><RoomPackageManagement /></PermissionRoute>} />
                    </Routes>
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </Router>
    </ConfigProvider>
  );
}

export default App;
